/* eslint-disable compat/compat */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { compose } from 'ramda';
import { getAvailableLanguage } from '@goeuro/app/client/fe-utils/src/localization/languages';

import callAPI from '../services/api/call-api';
import { GlobalStateContext } from '../contexts/globalState';
import { convertRawHtml, fixHtml } from '../shared/utils/legal';
import { API } from 'services/api';

export const useLazyLocales = (language: string) => {
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [updatedLocale, setLocale] = useState<string | null>(null);

  useEffect(() => {
    const lang = getAvailableLanguage(language);

    Promise.all([
      import(
        /* webpackChunkName: `formatjs-intl-pluralrules-polyfill-` */
        `@formatjs/intl-pluralrules/polyfill`
      ),
      import(
        /* webpackChunkName: `formatjs-intl-pluralrules-locale-data-` */
        `@formatjs/intl-pluralrules/locale-data/${lang}`
      ),
      import(
        /* webpackChunkName: "translation-en" */
        `translations/en`
      ),
      import(
        /* webpackChunkName: "translation-[request]" */
        `translations/${lang}`
      ),
      import(`@goeuro/app/client/user-profile-components/translations/${lang}`),
    ]).then(
      ([
        _,
        locale,
        b2bTranslationsEn,
        b2bTranslations,
        userProfileTranslations,
      ]) => {
        setTranslations(prev => ({
          ...prev,
          ...b2bTranslationsEn.default,
          ...b2bTranslations.default,
          ...userProfileTranslations,
        }));
        setLocale(language);
      }
    );
  }, [language]);

  return { translations, lang: updatedLocale };
};

export const useLegalPages = (mode: string) => {
  const [html, setHTML] = useState('');
  const { locale } = useGlobalState();

  useEffect(() => {
    (async () => {
      const payload = await axios({
        method: 'get',
        url: `/api/${mode}?locale=${locale}`,
        headers: {
          'Content-type': 'application/json',
        },
      });

      setHTML(payload.data.html);
    })();
  }, [locale]);

  return compose(fixHtml, convertRawHtml)(html);
};

export const useGlobalState = () => {
  const [state, dispatch] = React.useContext(GlobalStateContext);

  const setLocale = locale => {
    dispatch({
      type: 'SET_LOCALE',
      payload: locale,
    });
  };

  const setCurrency = currency => {
    dispatch({
      type: 'SET_CURRENCY',
      payload: currency,
    });
  };

  return {
    locale: state.locale,
    setLocale,
    currency: state.currency,
    setCurrency,
  };
};

export const useAPICall = ({
  resource,
  options,
  callback,
  urlParams,
}: {
  resource: keyof typeof API;
  options?: any;
  callback?: () => void;
  urlParams?: Record<string, string>;
}): [boolean, any, (data?: any) => void, any] => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);

  const handleRequest = async data => {
    try {
      setLoading(true);
      setError(null);
      const res = await callAPI(resource, urlParams, { ...options, data });
      setResponse(res);
      if (callback) callback();
    } catch (error) {
      setError(error as any);
    } finally {
      setLoading(false);
    }
  };
  return [loading, error, handleRequest, response];
};
